// src/pages/EventosCitas.js
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    addDoc,
    collection,
    doc,
    onSnapshot,
    serverTimestamp,
    Timestamp,
    updateDoc,
} from 'firebase/firestore';
import {
    FaCalendarAlt,
    FaChevronLeft,
    FaChevronRight,
    FaClock,
    FaExclamationTriangle,
    FaMapMarkerAlt,
    FaPlus,
    FaUsers,
} from 'react-icons/fa';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';
import { useRestriccionMorosidad } from '../hooks/useRestriccionMorosidad';
import { calcularEstadoMoroso, calcularAdeudoConInteres, esCasaCaseta, useConfiguracionFinanciera, esCasaMorosa } from '../utils/morosoUtils';
import { registerMovement } from '../utils/registerMovement';
import './EventosCitas.css';
import './EventosCitasMobile.css';

const COLLECTION_CANDIDATES = ['eventosCitas', 'eventos', 'citas'];

const EVENT_TYPE_META = {
    evento: { className: 'event-type-evento', label: 'Evento' },
    cita: { className: 'event-type-cita', label: 'Cita' },
    mantenimiento: { className: 'event-type-mantenimiento', label: 'Mantenimiento' },
    mantenimiento_preventivo: { className: 'event-type-mantenimiento', label: 'Mantenimiento' },
    junta: { className: 'event-type-junta', label: 'Junta' },
    reunion: { className: 'event-type-junta', label: 'Reunión' },
    default: { className: 'event-type-evento', label: 'Evento' },
};

const WEEKDAY_LABELS = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

const EventosCitas = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { userData, currentUser } = useAuth();
    const { showNotification } = useNotification();
    const { userDebt } = useRestriccionMorosidad();
    const fraccionamientoId = userData?.fraccionamientoId || userData?.fraccionamientoTd || null;
    const role = String(userData?.rol || '').toLowerCase();
    const isAdmin = ['superadmin', 'admin', 'manager', 'plataforma', 'admin_fracc', 'platformadmin'].includes(role);
    const isResidentMoroso = Boolean(userDebt?.moroso);

    const [currentMonth, setCurrentMonth] = useState(() => {
        const now = new Date();
        return new Date(now.getFullYear(), now.getMonth(), 1);
    });
    const [rawEvents, setRawEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [listFilter, setListFilter] = useState('todos');
    const [isModalOpen, setModalOpen] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);

    const visibleEvents = useMemo(
        () => rawEvents.filter((event) => {
            if (event?.soloAdministradores && !isAdmin) return false;
            // Blindaje de privacidad para morosos: eventos dirigidos solo a morosos no se muestran a residentes al corriente
            const esExclusivoMorosos = Boolean(event?.soloMorosos || event?.enviarSoloMorosos || event?.tipoDestinatarios === 'morosos');
            if (esExclusivoMorosos && !isAdmin && !isResidentMoroso) return false;

            // Blindaje de eventos con domicilios específicos
            const esPersonalizado = event?.tipoDestinatarios === 'personalizado' || (Array.isArray(event?.casasHabilitadas) && event.casasHabilitadas.length > 0);
            if (esPersonalizado && !isAdmin) {
                const userCasaId = userData?.casaId || userDebt?.casaData?.id;
                const userNumeroCasa = String(userData?.numeroCasa || userDebt?.casaData?.numero || '').trim().toLowerCase();
                const habilitadas = Array.isArray(event.casasHabilitadas) ? event.casasHabilitadas.map(String) : [];
                const numsHabilitados = Array.isArray(event.numerosCasasHabilitadas) ? event.numerosCasasHabilitadas.map(n => String(n).trim().toLowerCase()) : [];
                const matchCasaId = userCasaId && habilitadas.includes(String(userCasaId));
                const matchNum = userNumeroCasa && (numsHabilitados.includes(userNumeroCasa) || habilitadas.includes(userNumeroCasa));
                if (!matchCasaId && !matchNum) return false;
            }

            return true;
        }),
        [rawEvents, isAdmin, isResidentMoroso, userData, userDebt],
    );

    useEffect(() => {
        if (!fraccionamientoId) {
            setRawEvents([]);
            setLoading(false);
            return undefined;
        }

        let active = true;
        const unsubscribes = [];

        COLLECTION_CANDIDATES.forEach((collectionName) => {
            const ref = collection(db, 'fraccionamientos', fraccionamientoId, collectionName);
            try {
                const unsubscribe = onSnapshot(ref, (snapshot) => {
                    if (!active) return;
                    setRawEvents((prev) => {
                        const filtered = prev.filter((item) => item.__collection !== collectionName);
                        const mapped = snapshot.docs.map((docSnap) => ({
                            id: docSnap.id,
                            __collection: collectionName,
                            ...docSnap.data(),
                        }));
                        return [...filtered, ...mapped];
                    });
                    setLoading(false);
                }, (error) => {
                    console.warn(`No se pudo leer la colección ${collectionName}:`, error);
                    if (active) {
                        setRawEvents((prev) => prev.filter((item) => item.__collection !== collectionName));
                        setLoading(false);
                    }
                });
                unsubscribes.push(unsubscribe);
            } catch (error) {
                console.warn(`Error al suscribirse a ${collectionName}:`, error);
            }
        });

        return () => {
            active = false;
            unsubscribes.forEach((fn) => {
                if (typeof fn === 'function') {
                    fn();
                }
            });
        };
    }, [fraccionamientoId]);

    useEffect(() => {
        const shouldOpenModal = location.pathname.endsWith('/agendar');
        if (shouldOpenModal && !isAdmin) {
            navigate('/comunidad/eventos', { replace: true });
            return;
        }
        setModalOpen(shouldOpenModal && isAdmin);
    }, [location.pathname, isAdmin, navigate]);

    const eventsForMonth = useMemo(
        () => filterEventsByMonth(visibleEvents, currentMonth),
        [visibleEvents, currentMonth],
    );

    const eventsByDay = useMemo(
        () => buildEventsByDayMap(eventsForMonth),
        [eventsForMonth],
    );

    const calendarCells = useMemo(
        () => buildCalendarCells(currentMonth, eventsByDay),
        [currentMonth, eventsByDay],
    );

    const monthLabel = useMemo(
        () => formatMonthLabel(currentMonth),
        [currentMonth],
    );

    const upcomingEvents = useMemo(
        () => buildUpcomingEvents(visibleEvents),
        [visibleEvents],
    );

    const filteredUpcomingEvents = useMemo(() => {
        if (listFilter === 'eventos') {
            return upcomingEvents.filter((item) => item.filterCategory === 'eventos');
        }
        if (listFilter === 'citas') {
            return upcomingEvents.filter((item) => item.filterCategory === 'citas');
        }
        return upcomingEvents;
    }, [listFilter, upcomingEvents]);

    const handlePrevMonth = () => {
        setCurrentMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
    };

    const handleNextMonth = () => {
        setCurrentMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
    };

    const handleAgendarClick = () => {
        setSelectedEvent(null);
        navigate('/comunidad/eventos/agendar');
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setSelectedEvent(null);
        if (location.pathname.endsWith('/agendar')) {
            navigate('/comunidad/eventos', { replace: true });
        }
    };

    const handleSubmitEvento = async (payload) => {
        if (!fraccionamientoId) {
            showNotification('No se pudo identificar el fraccionamiento.', 'error');
            return false;
        }

        try {
            const targetCollection = resolveTargetCollection(payload.tipo);
            const eventosRef = collection(db, 'fraccionamientos', fraccionamientoId, targetCollection);

            const asistentesValue = normalizeAsistentes(payload.asistentes);

            const fechaEventoDate = payload.fechaInicio instanceof Date
                ? payload.fechaInicio
                : (payload.fecha instanceof Date ? payload.fecha : null);
            const fechaEventoTexto = fechaEventoDate
                ? fechaEventoDate.toLocaleDateString('es-MX')
                : '';

            const basePayload = {
                titulo: payload.titulo,
                tipo: payload.tipo,
                descripcion: payload.descripcion || null,
                fecha: Timestamp.fromDate(payload.fechaInicio),
                hora: payload.hora || null,
                ubicacion: payload.ubicacion || null,
                asistentes: asistentesValue,
                notas: payload.notas || null,
                responsable: payload.responsable || null,
                categoria: payload.categoria || payload.tipo,
                fraccionamientoId,
                actualizadoEn: serverTimestamp(),
            };

            if (payload.id && payload.__collection) {
                const docRef = doc(db, 'fraccionamientos', fraccionamientoId, payload.__collection, payload.id);
                await updateDoc(docRef, basePayload);
                showNotification('Evento actualizado correctamente.', 'success');
                try {
                    await registerMovement({
                        fraccionamientoId,
                        titulo: `Evento actualizado: ${payload.titulo}`,
                        descripcion: `${payload.tipo || 'evento'} · ${fechaEventoTexto}`,
                        categoria: 'Eventos',
                        tipoMovimiento: 'evento_actualizado',
                        origen: 'eventos',
                        metadata: {
                            eventoId: payload.id,
                            coleccion: payload.__collection,
                            tipoEvento: payload.tipo || null,
                            fechaEvento: fechaEventoDate,
                            ubicacion: payload.ubicacion || null,
                        },
                        userData,
                        currentUser,
                    });
                } catch (logError) {
                    console.error('EventosCitas: error al registrar movimiento de actualización de evento', logError);
                }
            } else {
                const docRef = await addDoc(eventosRef, {
                    ...basePayload,
                    creadoPorUid: userData?.uid || null,
                    creadoPorNombre: userData?.nombre || userData?.nombreCompleto || null,
                    creadoEn: serverTimestamp(),
                });
                showNotification('Evento agendado correctamente.', 'success');
                try {
                    await registerMovement({
                        fraccionamientoId,
                        titulo: `Nuevo evento: ${payload.titulo}`,
                        descripcion: `${payload.tipo || 'evento'} · ${fechaEventoTexto}`,
                        categoria: 'Eventos',
                        tipoMovimiento: 'evento_creado',
                        origen: 'eventos',
                        metadata: {
                            eventoId: docRef.id,
                            coleccion: targetCollection,
                            tipoEvento: payload.tipo || null,
                            fechaEvento: fechaEventoDate,
                            ubicacion: payload.ubicacion || null,
                        },
                        userData,
                        currentUser,
                    });
                } catch (logError) {
                    console.error('EventosCitas: error al registrar movimiento de nuevo evento', logError);
                }
            }

            handleCloseModal();
            return true;
        } catch (error) {
            console.error('Error al crear evento:', error);
            showNotification('No se pudo agendar el evento. Intenta nuevamente.', 'error');
            return false;
        }
    };

    return (
        <div className="eventos-page">
            <div className="eventos-header">
                <div className="eventos-header-left">
                    <div className="eventos-icon">
                        <FaCalendarAlt />
                    </div>
                    <div>
                        <h1>Eventos y Citas</h1>
                        <p>Consulta el calendario de eventos del fraccionamiento y agenda citas con la administración.</p>
                    </div>
                </div>
                {isAdmin && (
                    <button type="button" className="eventos-action-btn" onClick={handleAgendarClick}>
                        <FaPlus />
                        <span>Agendar Cita</span>
                    </button>
                )}
            </div>

            <div className="calendar-card">
                <div className="calendar-toolbar">
                    <button type="button" className="calendar-nav-btn" onClick={handlePrevMonth} aria-label="Mes anterior">
                        <FaChevronLeft />
                    </button>
                    <div className="calendar-month-label">{monthLabel}</div>
                    <button type="button" className="calendar-nav-btn" onClick={handleNextMonth} aria-label="Mes siguiente">
                        <FaChevronRight />
                    </button>
                </div>

                <div className="calendar-weekdays">
                    {WEEKDAY_LABELS.map((label) => (
                        <div key={label} className="calendar-weekday">{label}</div>
                    ))}
                </div>

                {loading ? (
                    <div className="calendar-loading">Cargando eventos...</div>
                ) : (
                    <>
                        <div className="calendar-grid">
                            {calendarCells.map((day) => (
                                <CalendarDay
                                    key={day.key}
                                    day={day}
                                    onSelectEvent={(event) => {
                                        if (isAdmin) {
                                            setSelectedEvent(event);
                                            navigate('/comunidad/eventos/agendar');
                                        } else {
                                            setSelectedEvent(event);
                                            setModalOpen(true);
                                        }
                                    }}
                                />
                            ))}
                        </div>
                        {eventsForMonth.length === 0 && (
                            <div className="calendar-empty">No hay eventos programados para este mes.</div>
                        )}
                    </>
                )}
            </div>

            <section className="next-events-section">
                <div className="next-events-header">
                    <h2>Próximos Eventos</h2>
                    <div className="next-events-filters">
                        <FilterPill label="Todos" isActive={listFilter === 'todos'} onClick={() => setListFilter('todos')} />
                        <FilterPill label="Eventos" isActive={listFilter === 'eventos'} onClick={() => setListFilter('eventos')} />
                        <FilterPill label="Citas" isActive={listFilter === 'citas'} onClick={() => setListFilter('citas')} />
                    </div>
                </div>

                {loading ? (
                    <div className="next-events-empty">Cargando próximos eventos...</div>
                ) : filteredUpcomingEvents.length === 0 ? (
                    <div className="next-events-empty">No hay actividades próximas.</div>
                ) : (
                    <div className="next-events-list">
                        {filteredUpcomingEvents.map((item) => (
                            <UpcomingEventCard
                                key={item.key}
                                data={item}
                                onSelect={() => {
                                    if (isAdmin) {
                                        setSelectedEvent(item.event);
                                        navigate('/comunidad/eventos/agendar');
                                    } else {
                                        setSelectedEvent(item.event);
                                        setModalOpen(true);
                                    }
                                }}
                            />
                        ))}
                    </div>
                )}
            </section>

            <ScheduleEventModal
                open={isModalOpen}
                onClose={handleCloseModal}
                onSubmit={handleSubmitEvento}
                initialData={selectedEvent}
                readOnly={!isAdmin}
                isAdmin={isAdmin}
                fraccionamientoId={fraccionamientoId}
            />
        </div>
    );
};

const CalendarDay = ({ day, onSelectEvent }) => {
    const { date, inCurrentMonth, isToday, events } = day;
    const displayNumber = date.getDate();
    const classNames = ['calendar-day'];

    if (!inCurrentMonth) classNames.push('is-outside');
    if (isToday) classNames.push('is-today');
    if (events.length > 0) classNames.push('has-events');

    return (
        <div className={classNames.join(' ')}>
            <div className="calendar-day-number">{displayNumber}</div>
            <div className="calendar-day-events">
                {events.slice(0, 3).map((event) => {
                    const meta = resolveEventMeta(event);
                    const title = getEventTitle(event);
                    const key = `${event.__collection}-${event.id}`;
                    const isPrivadoMorosos = Boolean(event?.soloMorosos || event?.enviarSoloMorosos);

                    return (
                        <button
                            type="button"
                            key={key}
                            className={`calendar-event-pill ${meta.className}`}
                            title={`${meta.label}: ${title}${isPrivadoMorosos ? ' (Privado: Solo residentes con adeudo)' : ''}`}
                            onClick={() => onSelectEvent?.(event)}
                        >
                            {isPrivadoMorosos ? `🔒 ${title}` : title}
                        </button>
                    );
                })}
                {events.length > 3 && (
                    <span className="calendar-event-pill calendar-event-more" title={`${events.length - 3} eventos adicionales`}>
                        +{events.length - 3} más
                    </span>
                )}
            </div>
        </div>
    );
};

const FilterPill = ({ label, isActive, onClick }) => (
    <button type="button" className={`filter-pill ${isActive ? 'active' : ''}`} onClick={onClick}>
        {label}
    </button>
);

const UpcomingEventCard = ({ data, onSelect }) => {
    const { event, meta, dateLabel, timeLabel, locationLabel, attendeesLabel } = data;
    const title = getEventTitle(event);
    const isPrivadoMorosos = Boolean(event?.soloMorosos || event?.enviarSoloMorosos);
    const className = `upcoming-event-card ${meta.className}`;

    return (
        <article className={className}>
            <div className="upcoming-event-title" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                <span>{title}</span>
                {isPrivadoMorosos && (
                    <span
                        style={{
                            fontSize: '0.72rem',
                            fontWeight: '600',
                            backgroundColor: '#fff3cd',
                            color: '#856404',
                            padding: '2px 8px',
                            borderRadius: '12px',
                            whiteSpace: 'nowrap',
                        }}
                        title="Evento privado para residentes con adeudo"
                    >
                        🔒 Solo Adeudos
                    </span>
                )}
            </div>
            <div className="upcoming-event-meta">
                {dateLabel && (
                    <span className="meta-item">
                        <FaCalendarAlt />
                        {dateLabel}
                    </span>
                )}
                {timeLabel && (
                    <span className="meta-item">
                        <FaClock />
                        {timeLabel}
                    </span>
                )}
                {locationLabel && (
                    <span className="meta-item">
                        <FaMapMarkerAlt />
                        {locationLabel}
                    </span>
                )}
                {attendeesLabel && (
                    <span className="meta-item">
                        <FaUsers />
                        {attendeesLabel}
                    </span>
                )}
            </div>
            <button type="button" className="upcoming-event-overlay" onClick={onSelect} aria-label="Ver detalles" />
        </article>
    );
};

const TIPOS_EVENTO = [
    { id: 'evento', label: 'Evento' },
    { id: 'cita', label: 'Cita' },
    { id: 'junta', label: 'Junta' },
    { id: 'mantenimiento', label: 'Mantenimiento' },
];

const ScheduleEventModal = ({ open, onClose, onSubmit, initialData, readOnly, isAdmin, fraccionamientoId }) => {
    const [tipo, setTipo] = useState('evento');
    const [titulo, setTitulo] = useState('');
    const [descripcion, setDescripcion] = useState('');
    const [fecha, setFecha] = useState('');
    const [hora, setHora] = useState('');
    const [ubicacion, setUbicacion] = useState('');
    const [asistentes, setAsistentes] = useState('');
    const [responsable, setResponsable] = useState('');
    const [enviando, setEnviando] = useState(false);

    const { cuotaMensual, mesesPermitidos } = useConfiguracionFinanciera(fraccionamientoId);
    const [totalMorosos, setTotalMorosos] = useState(null);

    useEffect(() => {
        if (!fraccionamientoId || !isAdmin) return;
        const ref = collection(db, 'fraccionamientos', fraccionamientoId, 'casas');
        const unsubscribe = onSnapshot(ref, (snapshot) => {
            const casas = snapshot.docs.map(d => ({ id: d.id, ...d.data() })).filter(c => !esCasaCaseta(c));
            const count = casas.filter(c => esCasaMorosa(c, cuotaMensual, mesesPermitidos)).length;
            setTotalMorosos(count);
        }, (err) => console.warn('Error al calcular morosos para evento:', err));

        return () => unsubscribe();
    }, [fraccionamientoId, cuotaMensual, mesesPermitidos]);

    useEffect(() => {
        if (open) {
            if (initialData) {
                const date = getEventDate(initialData);
                const formattedDate = date ? formatDateForInput(date) : '';
                const formattedTime = date ? formatTimeForInput(date) : '';

                setTipo(resolveEventTypeKey(initialData));
                setTitulo(initialData.titulo || initialData.title || '');
                setDescripcion(initialData.descripcion || initialData.description || initialData.notas || '');
                setFecha(formattedDate);
                setHora(initialData.hora || formattedTime || '');
                setUbicacion(initialData.ubicacion || initialData.lugar || '');
                setAsistentes(initialData.asistentes || initialData.aforo || initialData.cupos || '');
                setResponsable(initialData.responsable || '');
            } else {
                setTipo('evento');
                setTitulo('');
                setDescripcion('');
                setFecha('');
                setHora('');
                setUbicacion('');
                setAsistentes('');
                setResponsable('');
            }
            setEnviando(false);
        }
    }, [open, initialData]);

    if (!open) {
        return null;
    }

    const esValido = Boolean(tipo && titulo.trim() && fecha);

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!esValido || enviando || readOnly) {
            return;
        }

        setEnviando(true);
        const fechaSeleccionada = buildDateFromInputs(fecha, hora);
        if (!fechaSeleccionada) {
            setEnviando(false);
            return;
        }

        const payload = {
            tipo,
            titulo: titulo.trim(),
            descripcion: descripcion.trim(),
            fechaInicio: fechaSeleccionada,
            hora: hora || null,
            ubicacion: ubicacion.trim(),
            asistentes: asistentes,
            responsable: responsable.trim(),
            notas: descripcion.trim(),
            categoria: tipo,
            id: initialData?.id || null,
            __collection: initialData?.__collection || null,
        };

        await onSubmit(payload);
        setEnviando(false);
    };

    return (
        <div className="schedule-modal-backdrop">
            <div className="schedule-modal">
                <div className="schedule-modal-header">
                    <h2>{initialData ? (readOnly ? 'Detalle de evento' : 'Editar evento') : 'Agendar evento'}</h2>
                    <button type="button" className="schedule-modal-close" onClick={onClose}>
                        ×
                    </button>
                </div>

                {(initialData?.soloMorosos || initialData?.enviarSoloMorosos) && (
                    <div style={{
                        background: '#fff3cd',
                        border: '1px solid #ffeeba',
                        color: '#856404',
                        padding: '10px 14px',
                        borderRadius: '8px',
                        margin: '14px 24px 0',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        fontSize: '0.85rem',
                    }}>
                        <FaExclamationTriangle style={{ flexShrink: 0 }} />
                        <span><strong>Evento con Blindaje de Privacidad:</strong> Este evento únicamente es visible para residentes con adeudo pendiente y administración. Queda oculto para residentes al corriente.</span>
                    </div>
                )}

                <form className="schedule-form" onSubmit={handleSubmit}>
                    <div className="schedule-form-grid">
                        <label className="schedule-field full-width">
                            <span>Tipo</span>
                            <div className="schedule-type-switch">
                                {TIPOS_EVENTO.map((option) => (
                                    <button
                                        key={option.id}
                                        type="button"
                                        className={`type-pill ${tipo === option.id ? 'active' : ''}`}
                                        onClick={() => !readOnly && setTipo(option.id)}
                                        disabled={readOnly}
                                    >
                                        {option.label}
                                    </button>
                                ))}
                            </div>
                        </label>

                        <label className="schedule-field full-width">
                            <span>Título *</span>
                            <input
                                type="text"
                                value={titulo}
                                onChange={(e) => setTitulo(e.target.value)}
                                placeholder="Ej. Junta de vecinos"
                                required
                                disabled={readOnly}
                            />
                        </label>

                        <label className="schedule-field full-width">
                            <span>Descripción</span>
                            <textarea
                                rows={3}
                                value={descripcion}
                                onChange={(e) => setDescripcion(e.target.value)}
                                placeholder="Agrega detalles relevantes..."
                                disabled={readOnly}
                            />
                        </label>

                        <label className="schedule-field">
                            <span>Fecha *</span>
                            <input
                                type="date"
                                value={fecha}
                                onChange={(e) => setFecha(e.target.value)}
                                required
                                disabled={readOnly}
                            />
                        </label>

                        <label className="schedule-field">
                            <span>Hora</span>
                            <input
                                type="time"
                                value={hora}
                                onChange={(e) => setHora(e.target.value)}
                                disabled={readOnly}
                            />
                        </label>

                        <label className="schedule-field full-width">
                            <span>Ubicación</span>
                            <input
                                type="text"
                                value={ubicacion}
                                onChange={(e) => setUbicacion(e.target.value)}
                                placeholder="Ej. Salón de eventos"
                                disabled={readOnly}
                            />
                        </label>

                        <label className="schedule-field">
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                                <span>Asistentes esperados</span>
                                {isAdmin && (initialData?.soloMorosos || initialData?.enviarSoloMorosos || totalMorosos !== null) && totalMorosos !== null && (
                                    <button
                                        type="button"
                                        onClick={() => !readOnly && setAsistentes(String(totalMorosos))}
                                        title="Hacer clic para autocompletar con los residentes con adeudo"
                                        style={{
                                            background: '#fff3cd',
                                            border: '1px solid #ffeeba',
                                            color: '#856404',
                                            padding: '2px 8px',
                                            borderRadius: '10px',
                                            fontSize: '0.75rem',
                                            fontWeight: 600,
                                            cursor: readOnly ? 'default' : 'pointer',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '4px',
                                        }}
                                    >
                                        <FaUsers /> {totalMorosos} con adeudo
                                    </button>
                                )}
                            </div>
                            <input
                                type="number"
                                min="1"
                                value={asistentes}
                                onChange={(e) => setAsistentes(e.target.value)}
                                placeholder={isAdmin && totalMorosos !== null ? `Sugerido: ${totalMorosos}` : (readOnly ? "No especificado" : "Ej. 25")}
                                disabled={readOnly}
                            />
                        </label>

                        <label className="schedule-field">
                            <span>Responsable</span>
                            <input
                                type="text"
                                value={responsable}
                                onChange={(e) => setResponsable(e.target.value)}
                                placeholder="Nombre del responsable"
                                disabled={readOnly}
                            />
                        </label>
                    </div>

                    <div className="schedule-form-actions">
                        <button type="button" className="secondary" onClick={onClose}>
                            Cerrar
                        </button>
                        {isAdmin && !readOnly && (
                            <button type="submit" className="primary" disabled={!esValido || enviando}>
                                {enviando ? 'Guardando...' : initialData ? 'Actualizar' : 'Guardar'}
                            </button>
                        )}
                    </div>
                </form>
            </div>
        </div>
    );
};

function filterEventsByMonth(events, monthDate) {
    if (!monthDate) return [];
    const start = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
    const end = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0, 23, 59, 59, 999);

    return events.filter((event) => {
        const date = getEventDate(event);
        return date && date >= start && date <= end;
    }).sort((a, b) => {
        const da = getEventDate(a) || 0;
        const db = getEventDate(b) || 0;
        return da - db;
    });
}

function buildEventsByDayMap(events) {
    const map = new Map();
    events.forEach((event) => {
        const date = getEventDate(event);
        if (!date) return;
        const key = formatKey(date);
        if (!map.has(key)) {
            map.set(key, []);
        }
        map.get(key).push(event);
    });
    map.forEach((list) => {
        list.sort((a, b) => {
            const da = getEventDate(a) || 0;
            const db = getEventDate(b) || 0;
            return da - db;
        });
    });
    return map;
}

function buildCalendarCells(activeMonth, eventsByDay) {
    const year = activeMonth.getFullYear();
    const month = activeMonth.getMonth();
    const firstDayOfMonth = new Date(year, month, 1);
    const startWeekday = firstDayOfMonth.getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const totalCells = Math.ceil((startWeekday + daysInMonth) / 7) * 7;
    const todayKey = formatKey(new Date());

    const cells = [];
    for (let index = 0; index < totalCells; index += 1) {
        const dayNumber = index - startWeekday + 1;
        const cellDate = new Date(year, month, dayNumber);
        const canonicalKey = formatKey(cellDate);
        const inCurrentMonth = dayNumber >= 1 && dayNumber <= daysInMonth;

        cells.push({
            key: `${canonicalKey}-${index}`,
            date: cellDate,
            inCurrentMonth,
            isToday: canonicalKey === todayKey,
            events: inCurrentMonth ? (eventsByDay.get(canonicalKey) || []) : [],
        });
    }
    return cells;
}

function getEventDate(event) {
    if (!event) return null;
    const candidates = [
        event.fechaInicio,
        event.fecha,
        event.fechaEvento,
        event.fechaCita,
        event.fechaHora,
        event.inicio,
        event.start,
        event.startDate,
        event.date,
    ];

    for (let i = 0; i < candidates.length; i += 1) {
        const value = candidates[i];
        if (!value) continue;

        if (typeof value.toDate === 'function') {
            const date = value.toDate();
            if (!Number.isNaN(date.getTime())) return date;
        }

        const parsed = new Date(value);
        if (!Number.isNaN(parsed.getTime())) return parsed;
    }
    return null;
}

function formatKey(date) {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
}

function formatMonthLabel(date) {
    const formatter = new Intl.DateTimeFormat('es-MX', { month: 'long', year: 'numeric' });
    const label = formatter.format(date);
    return label.charAt(0).toUpperCase() + label.slice(1);
}

function resolveEventMeta(event) {
    const typeKey = resolveEventTypeKey(event);
    return EVENT_TYPE_META[typeKey] || EVENT_TYPE_META.default;
}

function resolveEventTypeKey(event) {
    const values = [event?.tipo, event?.categoria, event?.type, event?.estatus];
    for (let i = 0; i < values.length; i += 1) {
        const normalized = String(values[i] || '').toLowerCase();
        if (!normalized) continue;
        if (normalized.includes('cita')) return 'cita';
        if (normalized.includes('manten')) return 'mantenimiento';
        if (normalized.includes('junta') || normalized.includes('reun')) return 'junta';
        if (normalized.includes('event')) return 'evento';
    }
    return 'default';
}

function getEventTitle(event) {
    const candidates = [event?.titulo, event?.title, event?.nombre, event?.asunto, event?.descripcion];
    for (let i = 0; i < candidates.length; i += 1) {
        const value = candidates[i];
        if (typeof value === 'string' && value.trim()) {
            return value.trim();
        }
    }
    return 'Evento';
}
function buildUpcomingEvents(events) {
    const now = new Date();
    const list = events
        .map((event) => {
            const date = getEventDate(event);
            if (!date) return null;
            if (date < new Date(now.getFullYear(), now.getMonth(), now.getDate())) {
                return null;
            }

            const meta = resolveEventMeta(event);
            const dateLabel = formatUpcomingDate(date);
            const timeLabel = formatEventTime(event, date);
            const locationLabel = getEventLocation(event);
            const attendeesLabel = formatAttendeeLabel(event);
            return {
                key: `${event.__collection || 'event'}-${event.id}`,
                event,
                meta,
                date,
                dateLabel,
                timeLabel,
                locationLabel,
                attendeesLabel,
                filterCategory: resolveFilterCategory(event),
            };
        })
        .filter(Boolean)
        .sort((a, b) => a.date - b.date);

    return list;
}

function resolveFilterCategory(event) {
    const typeKey = resolveEventTypeKey(event);
    if (typeKey === 'cita' || typeKey === 'junta') {
        return 'citas';
    }
    return 'eventos';
}

function formatUpcomingDate(date) {
    const formatter = new Intl.DateTimeFormat('es-MX', {
        day: 'numeric',
        month: 'long',
    });
    const label = formatter.format(date);
    return label.charAt(0).toUpperCase() + label.slice(1);
}

function formatEventTime(event, date) {
    const candidates = [event?.hora, event?.horaInicio, event?.horaCita, event?.horaEvento, event?.time];
    for (let i = 0; i < candidates.length; i += 1) {
        const value = candidates[i];
        if (typeof value === 'string' && value.trim()) {
            return value.trim();
        }
    }

    const formatter = new Intl.DateTimeFormat('es-MX', {
        hour: '2-digit',
        minute: '2-digit',
    });
    return formatter.format(date);
}

function getEventLocation(event) {
    const candidates = [event?.lugar, event?.ubicacion, event?.sede, event?.location];
    for (let i = 0; i < candidates.length; i += 1) {
        const value = candidates[i];
        if (typeof value === 'string' && value.trim()) {
            return value.trim();
        }
    }
    return '';
}

function formatAttendeeLabel(event) {
    const candidates = [event?.asistentes, event?.aforo, event?.cupos, event?.participantes];
    for (let i = 0; i < candidates.length; i += 1) {
        const value = candidates[i];
        if (typeof value === 'number') {
            return value === 1 ? '1 asistente' : `${value} asistentes`;
        }
        if (typeof value === 'string' && value.trim()) {
            return value;
        }
    }
    return '';
}

function normalizeAsistentes(value) {
    if (value === null || value === undefined || value === '') {
        return null;
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
    }
    const parsed = Number(value);
    if (!Number.isNaN(parsed) && parsed > 0) {
        return parsed;
    }
    if (typeof value === 'string' && value.trim()) {
        return value.trim();
    }
    return null;
}

function formatDateForInput(date) {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
}

function formatTimeForInput(date) {
    const hours = `${date.getHours()}`.padStart(2, '0');
    const minutes = `${date.getMinutes()}`.padStart(2, '0');
    return `${hours}:${minutes}`;
}

function buildDateFromInputs(dateStr, timeStr) {
    if (!dateStr) {
        return null;
    }

    const [year, month, day] = dateStr.split('-').map(Number);
    if ([year, month, day].some((value) => Number.isNaN(value))) {
        return null;
    }

    const baseDate = new Date(year, (month || 1) - 1, day || 1, 0, 0, 0, 0);
    if (timeStr) {
        const [hours, minutes] = timeStr.split(':').map(Number);
        baseDate.setHours(hours || 0, minutes || 0, 0, 0);
    }

    return baseDate;
}

function resolveTargetCollection(tipo) {
    if (String(tipo).toLowerCase() === 'cita') {
        return 'citas';
    }
    return 'eventos';
}

export default EventosCitas;
